import {Form, Formik} from 'formik';
import {ChangeEvent, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Button from '../../../../../../components/button';
import CustomCheckbox from '../../../../../../components/mazraeKit/customCheckbox';
import downloadPDF from '../../../../../../global/helpers/downloadPDF';
import {
  IPersonalityType,
  IUserData,
} from '../../../../../../services/hookService/getUserCooperationAgreementData/types';
import {usePostSignCooperationAgreement} from '../../../../../../services/hookService/PostSignCooperationAgreement/usePostSignCooperationAgreement';
import getAgreementFormInitialValues from '../helper/getAgreementFormInitialValues';
import getFieldsByPersonType from '../helper/getFieldsByPersonType';
import {getValidationSchema} from '../helper/getValidationSchema';
import {EditableFieldBottomSheet} from './EditableFieldBottomSheet';
import PartnershipAgreementBottomSheet from './PartnershipAgreementBottomSheet';

const PDF_PATH_Individual =
  'https://public-pdfs.s3.ir-thr-at1.arvanstorage.ir/%D9%82%D8%B1%D8%A7%D8%B1%D8%AF%D8%A7%D8%AF%20%D9%85%D8%B4%D8%A7%D8%B1%DA%A9%D8%AA-%20%D8%AD%D9%82%DB%8C%D9%82%DB%8C.pdf?versionId=';
const PDF_PATH_Corporate =
  'https://public-pdfs.s3.ir-thr-at1.arvanstorage.ir/%D9%82%D8%B1%D8%A7%D8%B1%D8%AF%D8%A7%D8%AF%20%D9%85%D8%B4%D8%A7%D8%B1%DA%A9%D8%AA%20-%20%D8%AD%D9%82%D9%88%D9%82%DB%8C.pdf?versionId=';

const EditableFormList = ({
  formData,
  userType,
  showForm,
}: {
  formData?: IUserData;
  userType: IPersonalityType;
  showForm: boolean | null;
}) => {
  const [sheetStatus, setSheetStatus] = useState<'success' | 'pending' | null>(
    null,
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUserAgreed, setIsUserAgreed] = useState(false);

  const navigate = useNavigate();

  const initialValues = getAgreementFormInitialValues(formData, userType);
  const fields = getFieldsByPersonType(userType);
  const validationSchema = getValidationSchema(userType);

  const {mutate, isLoading} = usePostSignCooperationAgreement({
    onSuccess: () => {
      setSheetStatus('pending');
      setTimeout(() => {
        window.location.replace('https://app.emzano.com');
      }, 5000);
    },
  });

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      await downloadPDF(
        userType === IPersonalityType.Individual
          ? PDF_PATH_Individual
          : PDF_PATH_Corporate,
      );
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    setIsUserAgreed(false);
  }, [userType]);

  const handleOnClose = () => {
    setSheetStatus(null);
    if (sheetStatus === 'pending')
      window.location.replace('https://app.emzano.com');
    else navigate('/my-store-info');
  };

  if (!showForm) {
    return (
      <>
        <div className="sticky bottom-0 pt-2 pb-2 mt-auto w-full bg-white border-t border-gray-300">
          <Button
            className="w-full disabled:opacity-50 disabled:bg-gray-400 disabled:text-white disabled:cursor-not-allowed"
            type="button"
            disabled={true}
          >
            امضا قرارداد
          </Button>
        </div>
        <PartnershipAgreementBottomSheet
          status={sheetStatus}
          onClose={handleOnClose}
        />
      </>
    );
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        validateOnChange={true}
        validateOnBlur={true}
        validateOnMount={false}
        onSubmit={values => {
          mutate(values);
        }}
      >
        {({values, setFieldValue, errors, submitForm}) => (
          <>
            <Form className="overflow-y-auto flex-1">
              <div className="pb-4">
                <div className="p-4 w-full rounded-lg bg-zinc-100">
                  <h4 className="mb-3 text-sm font-medium text-gray-800">
                    اطلاعات فروشگاه
                  </h4>
                  {fields?.map((field, index) => (
                    <EditableFieldBottomSheet
                      key={field.name}
                      title={field.title}
                      name={field.name}
                      isLast={fields?.length === index + 1}
                      isRequired={field.isRequired}
                      fieldType={field.type || 'text'}
                    />
                  ))}
                </div>

                <div className="mt-6 w-full">
                  <CustomCheckbox
                    name="has_user_accepted_terms"
                    title={
                      'قرارداد مشارکت را مطالعه کردم و اطلاعات فروشگاه را تایید می‌کنم.'
                    }
                    customClass="gap-2 !items-start"
                    titleClass="text-sm text-gray-600"
                    checked={isUserAgreed}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setIsUserAgreed(e.target.checked);
                    }}
                  />
                </div>
              </div>
            </Form>

            <div className="flex flex-col gap-3 pt-2 w-full bg-white">
              <Button
                className="w-full"
                type="button"
                onClick={handleDownloadPdf}
                isLoading={isDownloading}
                disabled={isDownloading}
                variant="outline"
              >
                دانلود قرارداد
              </Button>

              <Button
                className="w-full disabled:opacity-50 disabled:bg-gray-400 disabled:text-white disabled:cursor-not-allowed"
                type="button"
                onClick={() => submitForm()}
                disabled={!isUserAgreed || isLoading}
                isLoading={isLoading}
              >
                امضا قرارداد
              </Button>
            </div>
          </>
        )}
      </Formik>

      <PartnershipAgreementBottomSheet
        status={sheetStatus}
        onClose={handleOnClose}
      />
    </>
  );
};

export default EditableFormList;
